import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Upload, Download, Image as ImageIcon, X, CreditCard, Eye, EyeOff } from "lucide-react";
import api, { formatApiError, notifyError, imageUrl } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FeatureSectionGuard from "@/components/FeatureSectionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const QRIS_ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const QRIS_MAX = 2 * 1024 * 1024;

function QrisManager({ qrisPath, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!QRIS_ALLOWED.includes(file.type)) {
      toast.error("Format tidak didukung. Gunakan JPG, JPEG, PNG, atau WEBP.");
      return;
    }
    if (file.size > QRIS_MAX) {
      toast.error("Ukuran melebihi 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/settings/qris", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Gambar QRIS berhasil diupload");
      onChanged?.();
    } catch (e) {
      toast.error(`Gagal upload: ${formatApiError(e)}`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setConfirmDel(false);
    try {
      await api.delete("/admin/settings/qris");
      toast.success("Gambar QRIS dihapus");
      onChanged?.();
    } catch (e) { notifyError(e); }
  };

  const downloadQris = () => {
    if (!qrisPath) return;
    const url = imageUrl(qrisPath);
    const a = document.createElement("a");
    a.href = url; a.download = "qris-pembayaran"; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-tour="settings-qris">
      <div className="flex items-center gap-2 mb-3">
        <ImageIcon className="text-[#E85D04]" size={18} />
        <h3 className="font-display font-bold">Gambar QRIS Pembayaran</h3>
      </div>
      <p className="text-xs text-[#5C5C5C] mb-4">
        Upload gambar QRIS toko Anda. Tampil di halaman checkout dan instruksi pembayaran pelanggan.
        Format: JPG, JPEG, PNG, WEBP. Maksimal 2 MB.
      </p>

      {qrisPath ? (
        <div className="space-y-3">
          <div className="inline-block p-3 rounded-2xl bg-[#FAF9F6] border border-[#E5E5E5]" data-testid="qris-preview">
            <img src={imageUrl(qrisPath)} alt="QRIS" className="max-w-[260px] max-h-[260px] rounded-lg" />
          </div>
          <div className="flex flex-wrap gap-2">
            <label className={`inline-flex items-center gap-2 cursor-pointer rounded-xl px-4 h-10 font-bold text-sm border border-[#E5E5E5] hover:bg-[#FAF9F6] ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="qris-change-btn">
              <Upload size={14} /> {uploading ? "Mengupload..." : "Ganti Gambar"}
              <input type="file" hidden accept="image/jpeg,image/jpg,image/png,image/webp" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            </label>
            <Button variant="outline" onClick={downloadQris} className="rounded-xl" data-testid="qris-download-btn"><Download size={14} className="mr-1" /> Download</Button>
            <Button variant="outline" onClick={() => setConfirmDel(true)} className="rounded-xl text-[#DC2F02]" data-testid="qris-delete-btn"><Trash2 size={14} className="mr-1" /> Hapus</Button>
          </div>
        </div>
      ) : (
        <label className={`flex flex-col items-center gap-2 cursor-pointer py-10 rounded-2xl border-2 border-dashed border-[#E5E5E5] hover:bg-[#FAF9F6] hover:border-[#E85D04] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="qris-upload-area">
          <Upload className="text-[#E85D04]" size={28} />
          <div className="text-sm font-semibold">{uploading ? "Mengupload..." : "Klik untuk upload QRIS"}</div>
          <div className="text-xs text-[#5C5C5C]">JPG, JPEG, PNG, WEBP · Maks 2 MB</div>
          <input type="file" hidden accept="image/jpeg,image/jpg,image/png,image/webp" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      )}

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus gambar QRIS?</AlertDialogTitle>
            <AlertDialogDescription>
              Pelanggan tidak akan melihat QRIS di halaman pembayaran sampai gambar baru diupload.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#DC2F02] hover:bg-[#b02701]">Ya, hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState({
    business_name: "", tagline: "", whatsapp_number: "", address: "",
    instagram: "", facebook: "", bank_accounts: [], qris_image: null,
  });

  const load = () => {
    api.get("/admin/settings").then((r) => {
      if (r.data) setS((prev) => ({ ...prev, ...r.data, bank_accounts: r.data.bank_accounts || [] }));
    });
  };

  useEffect(() => { load(); }, []);

  const upd = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const updBank = (i, k, v) => setS((x) => ({ ...x, bank_accounts: x.bank_accounts.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBank = () => setS((x) => ({ ...x, bank_accounts: [...x.bank_accounts, { _key: `bank-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, bank: "", account_number: "", account_name: "" }] }));
  const delBank = (i) => setS((x) => ({ ...x, bank_accounts: x.bank_accounts.filter((_, idx) => idx !== i) }));

  const save = async () => {
    try {
      // Don't send qris_image here — handled by dedicated endpoint
      const { qris_image, ...rest } = s;
      await api.put("/admin/settings", rest);
      toast.success("Pengaturan tersimpan");
    } catch (e) { notifyError(e); }
  };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-black mb-4">Pengaturan Toko</h1>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-3" data-tour="settings-store-info">
          <h3 className="font-display font-bold">Informasi Toko</h3>
          <div><Label>Nama Toko</Label><Input value={s.business_name || ""} onChange={(e) => upd("business_name", e.target.value)} data-testid="setting-business-name" /></div>
          <div><Label>Tagline</Label><Input value={s.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} /></div>
          <div><Label>No WhatsApp (62xxx)</Label><Input value={s.whatsapp_number || ""} onChange={(e) => upd("whatsapp_number", e.target.value)} placeholder="6281234567890" data-testid="setting-whatsapp" /></div>
          <div><Label>Alamat</Label><Textarea value={s.address || ""} onChange={(e) => upd("address", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Instagram</Label><Input value={s.instagram || ""} onChange={(e) => upd("instagram", e.target.value)} /></div>
            <div><Label>Facebook</Label><Input value={s.facebook || ""} onChange={(e) => upd("facebook", e.target.value)} /></div>
          </div>
        </section>

        <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-tour="settings-bank">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold">Rekening Bank</h3>
            <Button size="sm" variant="outline" onClick={addBank} className="rounded-xl"><Plus size={12} /> Tambah</Button>
          </div>
          <div className="space-y-3">
            {s.bank_accounts.map((b, i) => (
              <div key={b._key || `bank-existing-${i}`} className="bg-[#FAF9F6] rounded-xl p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={b.bank} onChange={(e) => updBank(i, "bank", e.target.value)} placeholder="Bank (BCA, Mandiri...)" />
                  <Input value={b.account_number} onChange={(e) => updBank(i, "account_number", e.target.value)} placeholder="No Rekening" />
                </div>
                <div className="flex gap-2">
                  <Input value={b.account_name} onChange={(e) => updBank(i, "account_name", e.target.value)} placeholder="Atas Nama" />
                  <Button size="icon" variant="outline" onClick={() => delBank(i)} className="rounded-xl text-[#DC2F02] shrink-0"><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
            {s.bank_accounts.length === 0 && <div className="text-xs text-[#5C5C5C]">Belum ada rekening</div>}
          </div>
        </section>

        <div className="lg:col-span-2">
          <QrisManager qrisPath={s.qris_image} onChanged={load} />
        </div>

        <div className="lg:col-span-2">
          <FeatureSectionGuard featureKey="integration.midtrans">
            <MidtransPanel />
          </FeatureSectionGuard>
        </div>
      </div>

      <div className="mt-6 sticky bottom-4">
        <Button onClick={save} className="brand-btn-primary rounded-xl h-12 px-6 font-bold shadow-lg" data-testid="save-settings-btn">
          <Save size={16} className="mr-1" /> Simpan Pengaturan
        </Button>
      </div>
    </AdminLayout>
  );
}

// ---------------- Midtrans Settings Panel (Phase 1 — Sandbox Snap) ----------------
function MidtransPanel() {
  const [cfg, setCfg] = useState({
    mode: "manual_only",
    midtrans: { enabled: false, sandbox: true, client_key: "", server_key_masked: "", _server_key_set: false, _client_key_set: false },
  });
  const [serverKeyInput, setServerKeyInput] = useState("");
  const [clientKeyInput, setClientKeyInput] = useState("");
  const [showServer, setShowServer] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/settings/midtrans").then((r) => {
      if (r.data) {
        setCfg(r.data);
        setClientKeyInput(r.data.midtrans?.client_key || "");
        setServerKeyInput(""); // never prefill server key
      }
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        mode: cfg.mode,
        midtrans: {
          enabled: !!cfg.midtrans.enabled,
          sandbox: !!cfg.midtrans.sandbox,
          // Client Key boleh kosong kalau belum diset; backend akan drop empty string
          client_key: clientKeyInput || "",
        },
      };
      // Server Key hanya dikirim kalau user input baru (plain text, non-mask)
      if (serverKeyInput && !serverKeyInput.includes("***")) {
        body.midtrans.server_key = serverKeyInput;
      }
      await api.put("/admin/settings/midtrans", body);
      toast.success("Setting Midtrans tersimpan");
      setServerKeyInput("");
      load();
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  const setMode = (mode) => setCfg((x) => ({ ...x, mode }));
  const setMt = (k, v) => setCfg((x) => ({ ...x, midtrans: { ...x.midtrans, [k]: v } }));

  return (
    <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="midtrans-panel">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="text-[#E85D04]" size={18} />
        <h3 className="font-display font-bold">Pembayaran Online — Midtrans</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest font-bold bg-[#FFEDD5] text-[#E85D04] px-2 py-1 rounded">Sandbox Phase 1</span>
      </div>
      <p className="text-xs text-[#5C5C5C] mb-4">
        Aktifkan pembayaran otomatis lewat Midtrans Snap (kartu kredit, GoPay, VA, dll). Saat <b>aktif</b>, pelanggan dapat membayar via popup Snap.
        <br />
        <span className="text-[#E85D04] font-semibold">Phase 1: status pesanan belum otomatis ter-update setelah bayar. Webhook akan ditambahkan di Phase 2.</span>
      </p>

      {/* Payment Mode */}
      <div className="space-y-2 mb-5">
        <Label className="text-xs uppercase tracking-wider font-bold text-[#5C5C5C]">Mode Pembayaran</Label>
        <RadioGroup value={cfg.mode} onValueChange={setMode} className="grid sm:grid-cols-3 gap-2">
          <Label className={`p-3 rounded-xl border cursor-pointer text-sm ${cfg.mode === "manual_only" ? "border-[#E85D04] bg-[#FFEDD5]/40" : "border-[#E5E5E5]"}`}>
            <RadioGroupItem value="manual_only" className="mr-2" data-testid="payment-mode-manual" />
            <b>Manual Saja</b>
            <div className="text-xs text-[#5C5C5C] mt-1">Hanya transfer & QRIS. (Aman default)</div>
          </Label>
          <Label className={`p-3 rounded-xl border cursor-pointer text-sm ${cfg.mode === "both" ? "border-[#E85D04] bg-[#FFEDD5]/40" : "border-[#E5E5E5]"}`}>
            <RadioGroupItem value="both" className="mr-2" data-testid="payment-mode-both" />
            <b>Manual + Midtrans</b>
            <div className="text-xs text-[#5C5C5C] mt-1">Pelanggan bisa pilih.</div>
          </Label>
          <Label className={`p-3 rounded-xl border cursor-pointer text-sm ${cfg.mode === "midtrans_only" ? "border-[#E85D04] bg-[#FFEDD5]/40" : "border-[#E5E5E5]"}`}>
            <RadioGroupItem value="midtrans_only" className="mr-2" data-testid="payment-mode-midtrans" />
            <b>Midtrans Saja</b>
            <div className="text-xs text-[#5C5C5C] mt-1">Manual disembunyikan.</div>
          </Label>
        </RadioGroup>
      </div>

      {/* Toggles */}
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="flex items-center justify-between bg-[#FAF9F6] rounded-xl p-3">
          <div>
            <div className="text-sm font-bold">Aktifkan Midtrans</div>
            <div className="text-xs text-[#5C5C5C]">Wajib aktif agar Snap popup muncul di checkout.</div>
          </div>
          <Switch checked={!!cfg.midtrans.enabled} onCheckedChange={(v) => setMt("enabled", v)} data-testid="midtrans-enabled-toggle" />
        </div>
        <div className="flex items-center justify-between bg-[#FAF9F6] rounded-xl p-3">
          <div>
            <div className="text-sm font-bold">Mode Sandbox</div>
            <div className="text-xs text-[#5C5C5C]">ON = sandbox (testing). OFF = production (uang asli).</div>
          </div>
          <Switch checked={!!cfg.midtrans.sandbox} onCheckedChange={(v) => setMt("sandbox", v)} data-testid="midtrans-sandbox-toggle" />
        </div>
      </div>

      {/* Keys */}
      <div className="space-y-3">
        <div>
          <Label>Client Key (publik, dipakai Snap.js)</Label>
          <Input
            value={clientKeyInput}
            onChange={(e) => setClientKeyInput(e.target.value)}
            placeholder="SB-Mid-client-..."
            className="font-mono text-xs"
            data-testid="midtrans-client-key-input"
          />
          {cfg.midtrans._client_key_set && (
            <div className="text-[10px] text-[#5C5C5C] mt-1">✓ Tersimpan</div>
          )}
        </div>
        <div>
          <Label>Server Key (rahasia, tidak akan ditampilkan kembali)</Label>
          <div className="relative">
            <Input
              type={showServer ? "text" : "password"}
              value={serverKeyInput}
              onChange={(e) => setServerKeyInput(e.target.value)}
              placeholder={cfg.midtrans._server_key_set ? cfg.midtrans.server_key_masked || "***" : "SB-Mid-server-..."}
              className="font-mono text-xs pr-10"
              data-testid="midtrans-server-key-input"
            />
            <button
              type="button"
              onClick={() => setShowServer((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#E85D04]"
              data-testid="midtrans-server-key-toggle"
              aria-label={showServer ? "Sembunyikan" : "Tampilkan"}
            >
              {showServer ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="text-[10px] text-[#5C5C5C] mt-1">
            {cfg.midtrans._server_key_set
              ? <>✓ Tersimpan ({cfg.midtrans.server_key_masked}). Biarkan kosong untuk mempertahankan kunci yang sudah ada.</>
              : <>Belum ada Server Key. Dapatkan dari <a href="https://dashboard.sandbox.midtrans.com/settings/config_info" target="_blank" rel="noreferrer" className="text-[#E85D04] underline">Dashboard Midtrans Sandbox</a>.</>
            }
          </div>
        </div>
      </div>

      <Button
        onClick={save}
        disabled={saving}
        className="mt-5 brand-btn-primary rounded-xl font-bold"
        data-testid="midtrans-save-btn"
      >
        <Save size={14} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan Pengaturan Midtrans"}
      </Button>

      <div className="mt-4 text-[11px] text-[#5C5C5C] bg-[#FAF9F6] rounded-xl p-3">
        🔒 <b>Keamanan:</b> Server Key disimpan di backend dan tidak pernah dikirim ke browser. Hanya Client Key (yang aman dipublikasi) yang dipakai oleh Snap.js.
      </div>
    </section>
  );
}
